// PriceOrderFilterButtonMobile.js - Closes only when both selections are made

import { useState, useEffect } from "react";
import { Checkbox } from "reusableComponents";
import { useTranslation } from "react-i18next";
import FiltersButton from "../FiltersButton";
import styles from "./styles.module.scss";

const PriceOrderFilterButtonMobile = ({
  selectedOrder = null,
  selectedOrderType = null,
  selectedOrderPriceType = null,
  onOrderChange,
}) => {
  const [t] = useTranslation("global");
  const [showOrderDropdown, setShowOrderDropdown] = useState(true); // Start open by default
  
  // Determine if active based on props
  const isActive = selectedOrderType === 'price' && (selectedOrder === 'asc' || selectedOrder === 'desc');

  // Available price types for ordering
  const priceTypeOptions = [
    { type: 'hour', key: 'search.orders.hour' },
    { type: 'half_day', key: 'search.orders.halfDay' },
    { type: 'day', key: 'search.orders.day' },
    { type: 'month', key: 'search.orders.month' }
  ];

  const toggleShowOrderDropdown = () => setShowOrderDropdown(!showOrderDropdown);

  // Check if we have a complete selection (both direction and price type)
  const hasCompleteSelection = () => {
    return isActive && selectedOrderPriceType !== null;
  };

  const handleSelectOrderDirection = (direction) => {
    if (onOrderChange) {
      // If clicking the same direction, remove the filter
      if (selectedOrder === direction && selectedOrderType === 'price') {
        onOrderChange({
          order: null,
          order_type: null,
          order_price_type: null
        });
        // Don't close dropdown when deselecting
      } else {
        // Set new order direction and type
        const newOrder = {
          order: direction,
          order_type: 'price'
        };
        
        // Keep the existing price type if it was already selected
        if (selectedOrderPriceType) {
          newOrder.order_price_type = selectedOrderPriceType;
        }
        
        onOrderChange(newOrder);
        
        // Only close dropdown if we have both selections (direction and price type)
        if (selectedOrderPriceType) {
          setShowOrderDropdown(false);
        }
        // Otherwise keep open to allow price type selection
      }
    }
  };

  const handleSelectPriceType = (priceType) => {
    if (onOrderChange) {
      // If clicking the same price type, remove it
      if (selectedOrderPriceType === priceType) {
        if (selectedOrder && selectedOrderType === 'price') {
          // Keep order direction, remove only price type
          onOrderChange({
            order: selectedOrder,
            order_type: 'price',
            order_price_type: null
          });
        } else {
          onOrderChange({ order_price_type: null });
        }
        // Don't close dropdown when deselecting
      } else {
        // Set new price type
        const newOrder = { order_price_type: priceType };
        
        // If we have order direction already, maintain it
        if (selectedOrder && selectedOrderType === 'price') {
          newOrder.order = selectedOrder;
          newOrder.order_type = 'price';
        }
        
        onOrderChange(newOrder);
        
        // Only close dropdown if we have both selections (direction and price type)
        if (selectedOrder && selectedOrderType === 'price') {
          setShowOrderDropdown(false);
        }
        // Otherwise keep open to allow direction selection
      }
    }
  };

  // Get button label based on current selection
  const getButtonLabel = () => {
    if (selectedOrderType === 'price') {
      const direction = selectedOrder === 'asc' 
        ? t('search.orders.lowToHigh') 
        : t('search.orders.highToLow');
      
      if (selectedOrderPriceType) {
        const priceTypeLabel = t(`search.orders.${selectedOrderPriceType}`);
        return `${direction} (${priceTypeLabel})`;
      }
      
      return direction;
    }
    return t('search.sortByPrice');
  };

  return (
    <div className={styles.orderWrapper}>
      <FiltersButton
        label={getButtonLabel()}
        isActive={hasCompleteSelection()}
        onClick={toggleShowOrderDropdown}
      />
      
      <div className={`${styles.orderDropdownBody} ${
        showOrderDropdown ? styles.visible : styles.hidden
      }`}>
        {/* Order Direction Section */}
        <div className={styles.orderSection}>
          <div className={styles.orderOption}>
            <Checkbox
              checked={selectedOrder === 'asc' && selectedOrderType === 'price'}
              onChange={() => handleSelectOrderDirection('asc')}
              label={t('search.orders.lowToHigh')}
              greyText={true}
            />
          </div>
          <div className={styles.orderOption}>
            <Checkbox
              checked={selectedOrder === 'desc' && selectedOrderType === 'price'}
              onChange={() => handleSelectOrderDirection('desc')}
              label={t('search.orders.highToLow')}
              greyText={true}
            />
          </div>
        </div>

        {/* Price Type Section - Only show if order direction is selected */}
        {(selectedOrder === 'asc' || selectedOrder === 'desc') && selectedOrderType === 'price' && (
          <div className={styles.priceTypeSection}>
            <div className={styles.priceTypeList}>
              {priceTypeOptions.map((option) => (
                <div key={option.type} className={styles.priceTypeOption}>
                  <Checkbox
                    checked={selectedOrderPriceType === option.type}
                    onChange={() => handleSelectPriceType(option.type)}
                    label={t(option.key)}
                    greyText={true}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PriceOrderFilterButtonMobile;